import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { useToast } from "../../../../shared/hooks";
import { ClientFollowUpService } from "../../../../shared/services/api/client/clientFollowUpService";
import { clientService } from "../../../../shared/services/api/client/clientService";
import { IBlockInvoiceListRow } from "../TableBlockInvoice";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../../shared/utils";

interface ModalNotifyBlockInvoiceProps {
	open: boolean;
	onClose: () => void;
	row: IBlockInvoiceListRow;
}

const monthMap: Record<string, string> = {
	"01": "Janeiro",
	"02": "Fevereiro",
	"03": "Março",
	"04": "Abril",
	"05": "Maio",
	"06": "Junho",
	"07": "Julho",
	"08": "Agosto",
	"09": "Setembro",
	"10": "Outubro",
	"11": "Novembro",
	"12": "Dezembro",
};

const formatReferenceLabel = (reference?: string | null) => {
	if (!reference) return "Referência não informada";

	return reference
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean)
		.map((item) => {
			const [year, month] = item.split("-");
			if (!year || !month || !monthMap[month]) return item;
			return `${monthMap[month]}/${year.slice(-2)}`;
		})
		.join(", ");
};

const buildDefaultMessage = (row: IBlockInvoiceListRow) => {
	const clientName = row.client?.name || row.client?.trade_name || "[RAZAO SOCIAL]";
	const clientDocument = row.client?.document ? globalMaskDocument(row.client.document) : "[CNPJ]";
	const invoiceLines = row.invoices
		.map((invoice) => `${formatReferenceLabel(invoice.reference)} – ${globalFormatCurrencyBRL(invoice.value)}`)
		.join("\n");
	const normalizeLink = (value?: string | null) => {
		const trimmedValue = value?.trim();
		return trimmedValue ? trimmedValue : null;
	};
	const openLinks = Array.from(new Set(row.invoices.map((invoice) => normalizeLink(invoice.url)).filter(Boolean)));
	const detailLinks = Array.from(new Set(row.invoices.map((invoice) => normalizeLink(invoice.summaryUrl) || normalizeLink(invoice.url)).filter(Boolean)));
	const openLinksText = openLinks.length > 0 ? openLinks.join("\n") : "[LINK PARA REGISTROS EM ABERTO]";
	const detailLinksText = detailLinks.length > 0 ? detailLinks.join("\n") : "[LINK DO DEMONSTRATIVO]";

	return [
		"Prezado(a),",
		"",
		clientName,
		clientDocument,
		"",
		"Estamos entrando em contato para informá-los sobre a inadimplência do pagamento dos boletos com vencimentos nos meses descritos abaixo:",
		invoiceLines || "[RELAÇÃO DE BOLETOS] – [VALOR]",
		"",
		"Segue o link para verificar os registros em aberto.",
		openLinksText,
		"",
		"Segue tambem o link do detalhamento.",
		detailLinksText,
		"",
		"De acordo com nossos registros, até o momento não recebemos o pagamento dos mesmos, caso tenha realizado os pagamentos favor nos responder com os comprovantes para que possamos realizar a baixa em nosso sistema.",
		"A falta de quitação deste compromisso implica em uma situação de inadimplência conforme estabelecido nos termos do contrato. Como resultado, somos obrigados a proceder com o cancelamento das garantias associadas ao seu contrato.",
		"Entendemos que imprevistos podem acontecer, e estamos dispostos a encontrar soluções para regularizar essa situação. Se houver qualquer dificuldade ou dúvida em relação ao pagamento, por favor, entre em contato conosco através do email setorfinanceiro@garantti.com.br",
	].join("\n");
};

export const ModalNotifyBlockInvoice = ({ open, onClose, row }: ModalNotifyBlockInvoiceProps) => {
	const toastHandler = useToast();
	const defaultMessage = useMemo(() => buildDefaultMessage(row), [row]);
	const [message, setMessage] = useState(defaultMessage);
	const [isSending, setIsSending] = useState(false);

	useEffect(() => {
		if (open) {
			setMessage(defaultMessage);
		}
	}, [defaultMessage, open]);

	const escapeHtml = (value: string) =>
		value
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/\"/g, "&quot;")
			.replace(/'/g, "&#39;");

	const convertMessageToHtml = (value: string) => {
		const normalizedLines = value.replace(/\r\n/g, "\n").split("\n");
		return normalizedLines.map((line) => `<p>${line.trim() ? escapeHtml(line) : "<br/>"}</p>`).join("");
	};

	const handleSendMessage = async () => {
		if (!row.clientId || !message.trim()) return;

		setIsSending(true);
		try {
			await ClientFollowUpService.addClientFollowUpNote(row.clientId, convertMessageToHtml(message));
			await clientService.markBlockNotificationSent(row.clientId);
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Notificacao enviada" content="A mensagem foi registrada com sucesso no follow-up do cliente." />,
			});
			onClose();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Nao foi possivel enviar a notificacao do cliente.";
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro ao notificar" content={errorMessage} />,
			});
		} finally {
			setIsSending(false);
		}
	};

	return (
		<Dialog open={open} onClose={isSending ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
					<Box>
						<Typography variant="h6">Notificar cliente</Typography>
						<Typography variant="body2" color="text.secondary">
							Mensagem inicial para {row.client?.name || row.client?.trade_name || "cliente"}
						</Typography>
					</Box>
					<IconButton onClick={onClose} aria-label="Fechar modal" disabled={isSending}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2}>
					<TextField
						multiline
						minRows={16}
						fullWidth
						label="Mensagem de notificacao"
						value={message}
						onChange={(event) => setMessage(event.target.value)}
					/>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} color="inherit" disabled={isSending}>
					Fechar
				</Button>
				<Button onClick={handleSendMessage} variant="contained" disabled={isSending || !message.trim()}>
					Enviar
				</Button>
			</DialogActions>
		</Dialog>
	);
};